import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { Map, List } from 'immutable';

import Button from '../helpers/Button';
import { AppPage } from '../AppPage';
import { Card } from '../helpers/Card';
import { GridCard } from '../helpers/GridCard';
import { UserTable } from './UserTable';
import { UserListFilters } from './UserListFilters';

import style from './style.module.css';

/**
 * User component.
 *
 * @param {object} props Props.
 * @param {Immutable.List} props.users List of users.
 * @param {Immutable.List} props.roles List of roles.
 * @param {Immutable.List} props.dashboardUserTitles List of title categories.
 * @param {Immutable.Map} props.filters User list filters.
 * @param {Immutable.List} props.suggestionUsers Users for module/supervisor suggestions.
 * @param {boolean} props.userLoading User table's loading state.
 * @param {Function} props.onFiltersChange Handle user list filters change.
 * @param {Function} props.onSortChange Handle user list sort change.
 * @param {Function} props.onAddClick Open the popup to add a new user.
 * @param {Function} props.onEdit Open the popup to edit a user, called with the user id.
 * @param {Function} props.onRemove2FaClick Handle remove 2FA click, called with the user id.
 * @param {Function} props.onExportUserList Export the user list.
 * @param {Immutable.List} props.tableCells List of table columns.
 */
const UserComponent = ({
  users,
  roles,
  dashboardUserTitles,
  filters,
  suggestionUsers,
  onFiltersChange,
  onSortChange,
  onAddClick,
  onEdit,
  onRemove2FaClick,
  userLoading,
  tableCells,
  onExportUserList,
}) => {
  const toolbar = (
    <div className={style.toolbar}>
      <Button onClick={onAddClick}>
        <i aria-hidden="true" className="fa fa-user-plus" /> Add New
      </Button>
      <Button onClick={onExportUserList}>
        <i aria-hidden="true" className="fa fa-download" /> Export User List
      </Button>
    </div>
  );

  return (
    <AppPage overline="Management" title="Users" toolbar={toolbar}>
      <Card title="Filters">
        <UserListFilters
          dashboardUserTitles={dashboardUserTitles}
          filters={filters}
          onFiltersChange={onFiltersChange}
          roles={roles}
          suggestionUsers={suggestionUsers}
        />
      </Card>

      <GridCard className={style.grid} dense>
        <UserTable
          onEdit={onEdit}
          onRemove2FaClick={onRemove2FaClick}
          onSortChange={onSortChange}
          tableCells={tableCells}
          userLoading={userLoading}
          users={users}
        />
      </GridCard>
    </AppPage>
  );
};

UserComponent.propTypes = {
  dashboardUserTitles: PropTypes.instanceOf(List).isRequired,
  filters: PropTypes.instanceOf(Map).isRequired,
  onAddClick: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onExportUserList: PropTypes.func.isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  onRemove2FaClick: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  roles: PropTypes.instanceOf(List).isRequired,
  suggestionUsers: PropTypes.instanceOf(List).isRequired,
  tableCells: PropTypes.instanceOf(List).isRequired,
  userLoading: PropTypes.bool.isRequired,
  users: PropTypes.instanceOf(List).isRequired,
};

export const User = memo(UserComponent);
